/**
 * «گزارش» در وب — قرینه‌ی `apps/mobile/src/features/report/ReportSheet.tsx`.
 *
 * سه جا استفاده می‌شود و هر سه یک کامپوننت‌اند: یک سیگنال، یک پیامِ چت، و یک
 * کاربر (مایگریشن 0135، WHOSUP-168). تنها فرقشان `target` و شکلِ دکمه است.
 *
 * ⚠ نامِ فایل مانده و محتوایش عوض شده: دیگر منو نیست، مودال است. دلیلِ پایین.
 *
 * ═══ چرا مودال، و نه منویی که کنارِ دکمه باز می‌شود ═══
 *
 * نسخه‌ی اولِ این فایل یک منوی کشویی بود، مثل `RespondMenu`. روی کارتِ سیگنال
 * و ردیفِ دوستان خوب کار می‌کرد و در **چت** خراب شد — آن‌قدر که سربرگِ گفتگو
 * قابلِ تست نبود. سه چیز با هم:
 *
 *   • حبابِ چت `max-width: 85%` است و `.menu` هم `min-width: 240px` دارد؛ منو
 *     از حباب می‌زد بیرون و روی صفحه‌ی باریک کلِ صفحه را افقی می‌کشید.
 *   • لنگرِ منو `z-index` داشت، یعنی یک stacking contextِ تازه می‌ساخت. پس
 *     منوی باز زیرِ **حبابِ بعدی** می‌رفت — آن یکی در DOM بعد از این می‌آید و
 *     `z-index: 40`ِ داخلی هیچ کاری از دستش برنمی‌آمد.
 *   • ماشه یک لینکِ زیرخط‌دار **زیرِ متنِ** هر پیام بود، پس هر پیامِ طرفِ
 *     مقابل یک خط بلندتر می‌شد و ستونِ چت پر از «Report» بود.
 *
 * مودالِ `position: fixed` هیچ‌کدام از این سه را ندارد: نه به عرضِ ظرفش کار
 * دارد، نه به ترتیبِ DOM، و ماشه‌اش می‌تواند یک آیکونِ کوچک باشد که به ارتفاعِ
 * حباب چیزی اضافه نکند. همان کاری که موبایل از اول با bottom sheet می‌کرد.
 */
import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { alreadyReported, reportRepo } from '@/core/data/reportRepo';
import { useSession } from '@/core/session';
import { t } from '@/core/lib/i18n';
import { reportError } from '@/core/lib/report';
import {
  REPORT_REASONS, REPORT_REASON_KEY, type ReportReason, type ReportTarget,
} from '@/core/lib/contentReport';
import { Card, Note } from '@/ui/kit';
import { FlagIcon } from '@/ui/icons';

/* ─────────────────────────── خودِ فرم ─────────────────────────── */

function ReportForm({ target, onClose }: { target: ReportTarget; onClose: () => void }) {
  const { profile } = useSession();
  const qc = useQueryClient();
  const [reason, setReason] = useState<ReportReason | null>(null);
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState<'added' | 'already' | null>(null);

  /**
   * گزارش‌های خودم — تا فرم بتواند بگوید «قبلاً گزارش داده‌ای» به‌جای اینکه
   * کاربر با فرستادن به قیدِ یکتاییِ دیتابیس بخورد.
   *
   * این کوئری تا مودال باز نشود اصلاً اجرا نمی‌شود، چون این کامپوننت تا آن
   * لحظه رندر نمی‌شود: صفحه‌ی چت ده‌ها ماشه‌ی گزارش دارد و کوئریِ خودکار برای
   * هرکدام یعنی ده‌ها رفت‌وبرگشت برای چیزی که معمولاً لازم نمی‌شود. کلیدِ
   * مشترک هم یعنی همه‌شان یک نتیجه را می‌بینند.
   */
  const mine = useQuery({
    queryKey: ['myReports'],
    queryFn: () => reportRepo.mine(),
    enabled: !!profile,
  });
  const already = !!mine.data && alreadyReported(mine.data, target);

  const send = useMutation({
    mutationFn: (r: ReportReason) => reportRepo.add(target, r, profile!.id, note),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['myReports'] });
      setSent(res);
    },
    onError: (e) => {
      reportError('web.report.add', e);
      setError(t('errorRetry'));
    },
  });

  // پیامِ پایانی جای فرم را می‌گیرد و مودال باز می‌ماند: بستنِ خودکار یعنی
  // کاربر نمی‌فهمد کارش گرفت یا نه.
  if (sent || already) {
    return (
      <>
        <h3>{t('reportTitle')}</h3>
        {/* `Note` پروپِ اضافه نمی‌گیرد؛ testid روی همان بسته‌بندی می‌نشیند. */}
        <div data-testid="report-done">
          <Note tone="ok">
            {sent === 'added' ? t('reportSentToast') : t('reportAlready')}
          </Note>
        </div>
        <button type="button" className="btn fill" data-testid="report-close" onClick={onClose}>
          {t('doneCheck')}
        </button>
      </>
    );
  }

  return (
    <>
      <h3>{t('reportTitle')}</h3>
      <p className="dim">{t('reportWhy')}</p>

      {/* دلیل‌ها چیپ‌اند و نه فهرستِ عمودی: هفت‌تا با هم دیده می‌شوند، پس کسی
          برای پیدا کردنِ دلیلِ خودش اسکرول نمی‌کند. */}
      <div className="chiprow">
        {REPORT_REASONS.map((r) => (
          <button key={r} type="button" className={`chip${reason === r ? ' on' : ''}`}
            data-testid={`report-reason-${r}`} onClick={() => setReason(r)}>
            {t(REPORT_REASON_KEY[r])}
          </button>
        ))}
      </div>

      {/* توضیح اختیاری است و دکمه بدونش هم کار می‌کند — ولی همین یک خط
          معمولاً تنها چیزی است که بعداً تصمیم گرفتن را ممکن می‌کند. */}
      <textarea className="input report-note" rows={3} value={note}
        data-testid="report-note" placeholder={t('reportNotePlaceholder')}
        onChange={(e) => setNote(e.target.value)} />

      {error ? <p className="error">{error}</p> : null}

      <div className="chiprow">
        <button type="button" className="btn fill" data-testid="report-submit"
          disabled={!reason || send.isPending}
          onClick={() => reason && send.mutate(reason)}>
          {send.isPending ? '…' : t('reportSubmit')}
        </button>
        <button type="button" className="btn" onClick={onClose}>
          {t('contactsPrivacyCancel')}
        </button>
      </div>

      <Note>{t('reportPrivateNote')}</Note>
    </>
  );
}

/* ─────────────────────────── ظرف ─────────────────────────── */

export function ReportDialog({ target, onClose }: {
  target: ReportTarget;
  onClose: () => void;
}) {
  // Escape می‌بندد — روی وب کاربر انتظارش را دارد. کلیکِ بیرون هم، پایین.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <Card className="modal" onClick={(e) => e.stopPropagation()}>
        <ReportForm target={target} onClose={onClose} />
      </Card>
    </div>
  );
}

/* ─────────────────────────── ماشه ─────────────────────────── */

/**
 * شکلِ دکمه، بسته به جایی که نشسته:
 *
 *   `btn`  دکمه‌ی متنیِ معمولی — کنارِ «اشتراک» و «تاریخچه» روی کارتِ سیگنال،
 *          هم‌شکلِ همان‌ها.
 *   `icon` دایره‌ی آیکونی — کنارِ سطلِ آشغالِ ردیفِ فهرستِ دوستان، هم‌شکلِ آن.
 *   `mini` کوچک‌ترین حالت، فقط یک پرچمِ کم‌رنگ — داخلِ حبابِ چت، روی همان خطی
 *          که نامِ فرستنده است. یک خطِ تازه به حباب اضافه نمی‌کند.
 */
type Look = 'btn' | 'icon' | 'mini';

export function ReportButton({ target, look = 'btn', label }: {
  target: ReportTarget;
  look?: Look;
  /** متنِ دکمه، و برچسبِ دسترس‌پذیریِ حالت‌های آیکونی. پیش‌فرض «گزارش». */
  label?: string;
}) {
  const { profile } = useSession();
  const [open, setOpen] = useState(false);
  const text = label ?? t('reportAction');

  // مهمان چیزی برای گزارش دادن ندارد: سیاستِ `cr_ins` نامِ گزارش‌دهنده را با
  // `auth.uid()` می‌سنجد، پس دکمه‌ای که کار نمی‌کند بهتر است نباشد.
  if (!profile) return null;

  return (
    <>
      {look === 'btn' ? (
        <button type="button" className="btn" data-testid="report-open"
          onClick={() => setOpen(true)}>{text}</button>
      ) : (
        <button type="button" className={look === 'icon' ? 'iconbtn2 danger' : 'report-mini'}
          aria-label={text} title={text} data-testid="report-open"
          onClick={() => setOpen(true)}>
          <FlagIcon size={look === 'icon' ? 16 : 13} />
        </button>
      )}

      {open ? <ReportDialog target={target} onClose={() => setOpen(false)} /> : null}
    </>
  );
}
